import React, { useState, useEffect } from "react";
import { Card, Modal } from "react-bootstrap";
import { Row, Col } from "react-bootstrap";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { useNavigate, useParams } from "react-router-dom";
import { menuList, roleMenuList, updateRoleMenuList } from "../../../services/admin";
import { toast } from "react-toastify";

const EditRolePermission = () => {
  const { id } = useParams();
  const [filterProduct, setFilterProduct] = useState([]);
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchMenuList();
  }, []);

  function fetchMenuList() {
    menuList().then((res) => {
      if (res.status === true) {
        let formattedMenus = [];

        res.data.Items.forEach((menu) => {
          if (menu.type === "link") {
            formattedMenus.push({
              id: menu.id,
              name: menu.title,
              parent: null,
              enabled: false, // Default state
              hasChildren: false,
            });
          } else if (menu.type === "sub" && menu.children) {
            formattedMenus.push({
              id: menu.id,
              name: menu.title,
              parent: null,
              enabled: false,
              hasChildren: true, // Parent with children cannot be toggled
            });

            menu.children.forEach((subMenu) => {
              formattedMenus.push({
                id: subMenu.id,
                name: subMenu.title,
                parent: menu.id, // Store parent ID
                enabled: false,
                hasChildren: false,
              });
            });
          }
        });



        roleMenuList(id).then((roleRes) => {
          if (roleRes?.status === true && roleRes.data?.Items) {

            const enabledMenuIds = new Set(
              roleRes.data.Items.flatMap((menu) =>
                menu.children ? [menu.id, ...menu.children.map((child) => child.id)] : [menu.id]
              )
            );

            const updatedMenus = formattedMenus.map((menu) => ({
              ...menu,
              enabled: enabledMenuIds.has(menu.id),
            }));

            setFilterProduct(updatedMenus);
          }
        });
      }
    });
  }

  // Handle individual checkbox toggle
  function handleSwitch(menuId) {
    setFilterProduct((prevMenus) => {
      let updatedMenus = prevMenus.map((menu) => {
        if (menu.id === menuId) {
          return { ...menu, enabled: !menu.enabled };
        }
        return menu;
      });

      const toggledMenu = updatedMenus.find((menu) => menu.id === menuId);

      if (toggledMenu) {
        if (toggledMenu.hasChildren) {
          // If it's a parent, toggle all children
          updatedMenus = updatedMenus.map((menu) =>
            menu.parent === menuId || menu.id === menuId
              ? { ...menu, enabled: toggledMenu.enabled }
              : menu
          );
        } else if (toggledMenu.parent) {
          // If it's a child and checked, ensure parent is checked
          if (toggledMenu.enabled) {
            updatedMenus = updatedMenus.map((menu) =>
              menu.id === toggledMenu.parent ? { ...menu, enabled: true } : menu
            );
          } else {
            // If it's a child and unchecked, check if all siblings are unchecked
            const parent = updatedMenus.find((menu) => menu.id === toggledMenu.parent);
            const allChildrenDeselected = updatedMenus
              .filter((menu) => menu.parent === parent.id)
              .every((child) => !child.enabled);

            if (allChildrenDeselected) {
              updatedMenus = updatedMenus.map((menu) =>
                menu.id === parent.id ? { ...menu, enabled: false } : menu
              );
            }
          }
        }
      }

      return updatedMenus;
    });
  }


  const handleSave = () => {
    const selectedIds = filterProduct
      .filter((menu) => menu.enabled) // Get only checked items
      .map((menu) => menu.id); // Extract IDs

    updateRoleMenuList(id, selectedIds).then((response) => {
      if (response.message) {
        toast.success(response.message);
        navigate("/settings/rolepermission");
        fetchMenuList();
      }
    });
    // You can send `selectedIds` to an API or perform other actions here
  };

  // Handle "Check All" functionality
  const handleCheckAll = (event) => {
    const isChecked = event.target.checked;
    setFilterProduct((prevMenus) =>
      prevMenus.map((menu) => ({ ...menu, enabled: isChecked }))
    );
  };

  // Check if all menus (parent & children) are selected
  const allChecked = filterProduct.every((menu) => menu.enabled);

  const columns = [
    {
      accessorKey: "id",
      header: () => (
        <input
          type="checkbox"
          className="styled-checkbox"
          onChange={handleCheckAll}
          checked={allChecked}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="styled-checkbox"
          checked={row.original.enabled}
          // disabled={row.original.hasChildren}
          onChange={() => handleSwitch(row.original.id)}
        />
      ),
      className: "checkbox-cell",
    },
    {
      accessorKey: "name",
      header: "Menu",
      cell: ({ row }) => (
        <span className={row.original.parent === null ? "parent-row" : ""}>
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: "parent",
      header: "Parent Menu",
      cell: ({ row }) => {
        const parentMenu = filterProduct.find((menu) => menu.id === row.original.parent);
        return parentMenu ? parentMenu.name : "-";
      },
    },
  ];

  const table = useReactTable({
    data: filterProduct,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const RoleName = (id === '2') ? 'Admin' : id === '4' ? 'Student' : id === '5' ? 'Teacher' : 'Role';


  return (
    <div>
      <h2>{'Role Permission List'}-{RoleName}</h2>
      <Row className="mx-0 mt-3">
        <Col lg={12}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-end">
              <button onClick={() => navigate("/settings/rolepermission")} className="btn btn-secondary me-2">
                  {'Back'}
                </button>
                <button onClick={handleSave} className="btn btn-success">
                  {'Save'}
                </button>
              </div>

              {/* Table Container for Scrollability */}
              <div className="table-container mt-3">
                <table className="roles-table">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => {
                      const isParent = row.original.parent === null;
                      return (
                        <tr key={row.id} className={isParent ? "parent-row" : ""}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal */}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <h4>Manage Role</h4>
        </Modal.Header>
        <Modal.Body>
          <label>Role Name:</label>
          <input type="text" className="form-control" />
          {errorMessage && <p className="text-danger">{errorMessage}</p>}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-success">Save</button>
          <button className="btn btn-secondary" onClick={() => setShow(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditRolePermission;
